import React, { useEffect, useRef, useState } from 'react';
import { downloadLegalBoardCertificateDocument, readCertificatePdf, removeLegalBoardCertificateDocument, uploadLegalBoardCertificateDocument } from './api';
import { formatDateCa, newRequestId } from './legalBoardUi';

/**
 * `onHowToKnow` opens the explanation of how to request the registry
 * certificate; it is only reachable from here once the board is registered
 * (the step-1 shortcut disappears at that point). `describe={false}` drops
 * the line saying what the document is, for places that already explain it;
 * `dropHint` replaces the small print of the drop zone.
 */
function LegalBoardCertificateDocument({ term, userId, canManage = false, disabled = false, onReload, onHowToKnow, describe = true, dropHint = 'Opcional · PDF fins a 10 MB' }) {
    const [documentFile, setDocumentFile] = useState(term.certificateDocument || null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [showOptions, setShowOptions] = useState(false);
    const [dragging, setDragging] = useState(false);
    const dragDepth = useRef(0);
    const inFlight = useRef(false);
    const pendingUpload = useRef(null);
    const pendingRemoval = useRef(null);
    const editable = canManage && term.lifecycleStatus === 'current';
    const inputId = `legal-board-certificate-file-${term.id}`;
    const optionsId = `legal-board-certificate-options-${term.id}`;

    useEffect(() => { setDocumentFile(term.certificateDocument || null); }, [term.certificateDocument]);

    const run = async (action) => {
        if (inFlight.current || disabled) return;
        inFlight.current = true;
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await action();
        } catch (actionError) {
            setError(actionError.message || 'No s’ha pogut completar l’acció. Torna-ho a provar.');
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    const uploadFiles = (fileList) => {
        if (!editable || inFlight.current || disabled) return;
        const files = Array.from(fileList || []);
        if (!files.length) return;
        if (files.length !== 1) {
            setNotice(null);
            setError('Adjunta un sol certificat en PDF.');
            return;
        }
        run(async () => {
            const file = await readCertificatePdf(files[0]);
            // Reuse the request id if the same upload is retried after a network error.
            if (pendingUpload.current?.file.name !== file.name || pendingUpload.current?.file.data !== file.data) {
                pendingUpload.current = { file, requestId: newRequestId() };
            }
            const response = await uploadLegalBoardCertificateDocument({ userId, termId: term.id, ...pendingUpload.current });
            setDocumentFile(response.term.certificateDocument);
            pendingUpload.current = null;
            pendingRemoval.current = null;
            setShowOptions(false);
            setNotice('Certificat guardat. Ja el tens a mà.');
            if (onReload) await onReload();
        });
    };

    const upload = (event) => {
        uploadFiles(event.target.files);
        event.target.value = '';
    };

    const dragEnter = (event) => {
        event.preventDefault();
        if (inFlight.current || disabled) return;
        dragDepth.current += 1;
        setDragging(true);
    };

    const dragLeave = (event) => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDragging(false);
    };

    const drop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepth.current = 0;
        setDragging(false);
        uploadFiles(event.dataTransfer.files);
    };

    const remove = () => {
        if (!window.confirm('Vols eliminar el certificat guardat d’aquest mandat?')) return;
        run(async () => {
            if (!pendingRemoval.current) pendingRemoval.current = newRequestId();
            const response = await removeLegalBoardCertificateDocument({ userId, termId: term.id, requestId: pendingRemoval.current });
            setDocumentFile(response.term.certificateDocument);
            pendingRemoval.current = null;
            pendingUpload.current = null;
            setShowOptions(false);
            setNotice('Certificat eliminat.');
            if (onReload) await onReload();
        });
    };

    const download = () => run(() => downloadLegalBoardCertificateDocument({ userId, termId: term.id, name: documentFile.name }));

    if (!documentFile && !editable) return null;

    const uploadControl = (
        <span className="legal-board-certificate-upload">
            <input id={inputId} className="legal-board-sr-only" type="file" accept=".pdf,application/pdf" onChange={upload} disabled={busy || disabled} aria-label={documentFile ? 'Substituir el certificat' : 'Adjuntar el certificat'} />
            <label htmlFor={inputId} className="legal-board-button link small" aria-disabled={busy || disabled}>
                {documentFile ? 'Substituir el certificat' : 'Adjuntar certificat'}
            </label>
        </span>
    );

    return (
        <section className={`legal-board-certificate-document${documentFile ? ' has-document' : ''}`} aria-label="Certificat d’inscripció" aria-busy={busy}>
            {documentFile ? <div className="legal-board-certificate-row">
                <svg className="legal-board-certificate-icon" width="18" height="20" viewBox="0 0 20 22" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M11 2H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8L11 2Z" />
                    <path d="M11 2v6h6M6 12h8M6 16h5" />
                </svg>
                <div className="legal-board-certificate-content">
                    <strong>Certificat d’inscripció</strong>
                    <p className="legal-board-certificate-filename legal-board-muted" title={documentFile.name}>{documentFile.name}</p>
                </div>
                <div className="legal-board-certificate-row-actions">
                    <button type="button" className="legal-board-button primary legal-board-certificate-download" onClick={download} disabled={busy || disabled} aria-label="Descarregar el certificat">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M10 2v10m-4-4 4 4 4-4M3 13v4h14v-4" />
                        </svg>
                        <span>Descarregar<span className="legal-board-certificate-download-subject"> certificat</span></span>
                    </button>
                    {editable ? (
                        <button type="button" className="legal-board-certificate-more" aria-label="Més opcions del certificat" title="Més opcions" aria-expanded={showOptions} aria-controls={optionsId} onClick={() => setShowOptions(value => !value)} disabled={busy || disabled}>
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <circle cx="4" cy="10" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="16" cy="10" r="1.5" />
                            </svg>
                        </button>
                    ) : null}
                </div>
            </div> : (
                <div
                    className={`legal-board-certificate-dropzone${dragging ? ' is-dragging' : ''}`}
                    role="group"
                    aria-label="Pujar el certificat d’inscripció"
                    aria-disabled={busy || disabled}
                    onDragEnter={dragEnter}
                    onDragLeave={dragLeave}
                    onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = busy || disabled ? 'none' : 'copy';
                    }}
                    onDrop={drop}
                >
                    <input id={inputId} className="legal-board-sr-only" type="file" accept=".pdf,application/pdf" onChange={upload} disabled={busy || disabled} aria-label="Adjuntar el certificat" />
                    <label htmlFor={inputId} className="legal-board-certificate-drop-target">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 16V3m-5 5 5-5 5 5M4 16v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
                        </svg>
                        <strong>{busy ? 'Guardant el certificat…' : dragging ? 'Deixa’l anar per guardar-lo' : 'Arrossega aquí el certificat'}</strong>
                        <span>o <span className="legal-board-certificate-select">selecciona un PDF</span></span>
                        <span className="legal-board-certificate-drop-hint">{dropHint}</span>
                    </label>
                </div>
            )}
            {!documentFile && describe ? (
                // Outside the dropzone label on purpose: reading what the document is must not open the file picker.
                <p className="legal-board-certificate-what legal-board-muted legal-board-small">
                    Certificació de dades registrals del Registre d’Associacions (tràmit amb taxa)
                    {typeof onHowToKnow === 'function' ? (
                        <>
                            {' · '}
                            <button type="button" className="legal-board-inline-action" onClick={onHowToKnow} disabled={busy || disabled}>com demanar-la</button>
                        </>
                    ) : null}
                </p>
            ) : null}
            {editable && documentFile && showOptions ? (
                <div id={optionsId} className="legal-board-certificate-options">
                    <p className="legal-board-muted">
                        PDF · {Math.max(1, Math.ceil(documentFile.size / 1024))} KB
                        {documentFile.uploadedAt ? ` · Guardat el ${formatDateCa(documentFile.uploadedAt)}` : ''}
                        {documentFile.uploadedBy ? ` per ${documentFile.uploadedBy}` : ''}
                    </p>
                    <div className="legal-board-certificate-attach">
                        {uploadControl}
                        <button type="button" className="legal-board-button link small danger" onClick={remove} disabled={busy || disabled}>Eliminar el certificat</button>
                    </div>
                </div>
            ) : null}
            {busy ? <p role="status" className="legal-board-muted legal-board-small">Processant el certificat…</p> : null}
            {notice ? <p role="status" className="legal-board-small">{notice}</p> : null}
            {error ? <div role="alert" className="legal-board-alert error">{error}</div> : null}
        </section>
    );
}

export default LegalBoardCertificateDocument;
